import type {
  Bewaking, Bijlage, Deling, Gebeurtenis, Geheim, Ik, InstellingenStand, Item, Link, MeekijkStand, MeekijkVerzoek, Mens,
  NamensDuur, NamensSessie, NamensStart, Niveau, OverdrachtPunt, Pakket, PersoonCode,
  RdwUitkomst, Start, Systeem, Ticket, TicketBericht, Toegang, Vestiging, ZoekUitkomst,
} from '../../gedeeld/types'

/* ===========================================================================
 *  Praten met de eigen server
 *
 *  Alles loopt via de Worker op hetzelfde adres; de browser heeft nergens
 *  anders een sleutel voor. Wat hij heeft is een koekje, en dat gaat vanzelf
 *  mee.
 *
 *  Elke aanroep gooit bij een mislukking, met de tekst van de server erbij,
 *  en het scherm laat die zien. "Je ticket is gemeld" zeggen terwijl het
 *  nooit aankwam, is de fout die hier het meest kost: iemand wacht dan op
 *  IT, en IT weet van niets.
 * =========================================================================== */

export class Fout extends Error {
  constructor(public status: number, bericht: string) { super(bericht) }
}

let bijUitgelogd: (() => void) | null = null
export function opUitgelogd(f: () => void) { bijUitgelogd = f }

/* Gaat af als de Worker zegt "bevestig eerst dat jij het bent" (428, zie
   worker/sessie.ts onder "Bevestigd"). De app toont dan een venster. */
let bijBevestigen: ((waarom?: string) => void) | null = null
export function opBevestigen(f: (waarom?: string) => void) { bijBevestigen = f }

async function lees<T>(a: Response): Promise<T> {
  let lijf: { ok?: boolean; fout?: string } & Record<string, unknown> = {}
  try { lijf = await a.json() } catch { /* geen JSON: dan volgt hieronder de status */ }
  if (a.status === 401) {
    bijUitgelogd?.()
    throw new Fout(401, 'Je bent uitgelogd.')
  }
  if (a.status === 428 && (lijf as { bevestigen?: boolean }).bevestigen) {
    const waarom = (lijf as { waarom?: unknown }).waarom
    bijBevestigen?.(typeof waarom === 'string' ? waarom : undefined)
    throw new Fout(428, lijf.fout ?? 'Bevestig eerst dat jij het bent.')
  }
  if (!a.ok || lijf.ok === false) throw new Fout(a.status, lijf.fout ?? `De server gaf ${a.status}.`)
  return lijf as T
}

/** Een actie van de functie it (zie worker/gegevens.ts voor de lijst). */
export async function i<T = Record<string, unknown>>(actie: string, lijf: Record<string, unknown> = {}): Promise<T> {
  let a: Response
  try {
    a = await fetch('/api/i', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ ...lijf, actie }),
    })
  } catch {
    throw new Fout(0, 'Geen verbinding. Controleer je internet en probeer het opnieuw.')
  }
  return lees<T>(a)
}

/**
 * Meekijken stoppen terwijl de pagina weggaat (tabblad dicht, ververst).
 * keepalive: de vraag gaat ook nog weg als de pagina er al niet meer is.
 * Geen antwoord om op te wachten; lukt het niet, dan zet de wekker hem op
 * verlopen.
 */
export function meekijkStopBijWeggaan(id: string): void {
  try {
    void fetch('/api/i', {
      method: 'POST',
      keepalive: true,
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id, actie: 'meekijk-stop' }),
    }).catch(() => { /* zie boven */ })
  } catch { /* zie boven */ }
}

/* ------------------------------------------------------------------ *
 *  Wie ben ik
 * ------------------------------------------------------------------ */

export interface Sessie { ik: Ik; verlooptOp: number }

/** Null als er niemand is ingelogd. Gooit niet: dit is de eerste vraag. */
export async function haalSessie(): Promise<Sessie | null> {
  try {
    const a = await fetch('/api/sessie', { headers: { Accept: 'application/json' } })
    if (!a.ok) return null
    const lijf = await a.json() as { ok?: boolean } & Sessie
    return lijf.ok ? lijf : null
  } catch {
    return null
  }
}

export async function uitloggen(): Promise<void> {
  try { await fetch('/api/uitloggen', { method: 'POST' }) } catch { /* vervalt vanzelf */ }
}

/* ------------------------------------------------------------------ *
 *  Bijlagen
 * ------------------------------------------------------------------ */

export async function upload(soort: 'ticket' | 'item', id: string, bestand: File): Promise<Bijlage> {
  if (bestand.size > 15 * 1024 * 1024) throw new Fout(413, `${bestand.name} is groter dan 15 MB.`)
  const q = new URLSearchParams({ soort, id, naam: bestand.name })
  let a: Response
  try {
    a = await fetch('/api/upload?' + q.toString(), {
      method: 'POST',
      headers: { 'Content-Type': bestand.type || 'application/octet-stream' },
      body: bestand,
    })
  } catch {
    throw new Fout(0, 'Geen verbinding; het bestand is niet verstuurd.')
  }
  return (await lees<{ bijlage: Bijlage }>(a)).bijlage
}

export const bestandAdres = (id: string, inline = false) => `/api/bestand/${id}${inline ? '?inline=1' : ''}`

/* ------------------------------------------------------------------ *
 *  Alles van de functie, met zijn vorm
 * ------------------------------------------------------------------ */

export interface TicketDetail { ticket: Ticket; berichten: TicketBericht[]; bijlagen: Bijlage[] }
export interface ItemDetail {
  item: Item; codes: Geheim[]; tickets: Ticket[]; delingen: Deling[]; bijlagen: Bijlage[]; geschiedenis: Gebeurtenis[]
  bewaking: Bewaking | null
}
export interface GeheimDetail { geheim: Geheim; delingen: Deling[]; geschiedenis: Gebeurtenis[] }
export interface SysteemDetail { systeem: Systeem; toegang: Toegang[]; codes: Geheim[]; tickets: Ticket[]; geschiedenis: Gebeurtenis[] }
export interface PersoonDetail {
  persoon: Mens; items: Item[]; tickets: Ticket[]; toegang: Toegang[]; codes: PersoonCode[]
}

export type TicketFilter = 'open' | 'afgerond' | 'alles' | 'mijn' | 'voor_mij' | 'niet_toegewezen'

export const api = {
  /* ontwikkelaar: alleen als de functie het meestuurt (namens kijken, 0244);
     anders kijkt App.tsx naar de rollen in de sessie. */
  ik: () => i<{ beheerder: boolean; instellingen?: boolean; ontwikkelaar?: boolean; kluis: boolean; vestigingen: Vestiging[] }>('ik'),
  mensen: () => i<{ mensen: Mens[] }>('mensen').then((x) => x.mensen),
  start: () => i<Start>('start'),
  zoeken: (q: string) => i<ZoekUitkomst>('zoeken', { q }),

  tickets: (filter: TicketFilter, q?: string) => i<{ tickets: Ticket[] }>('tickets', { filter, q }).then((x) => x.tickets),
  ticket: (id: string) => i<TicketDetail>('ticket', { id }),
  ticketMelden: (ticket: Partial<Ticket>) => i<{ ticket: Ticket }>('ticket-melden', { ticket }).then((x) => x.ticket),
  ticketBijwerken: (id: string, wijzig: Partial<Ticket>) => i<{ ticket: Ticket }>('ticket-bijwerken', { id, wijzig }).then((x) => x.ticket),
  ticketReageren: (id: string, tekst: string, extra: { intern?: boolean; status?: string } = {}) =>
    i<{ bericht: TicketBericht }>('ticket-reageren', { id, tekst, ...extra }).then((x) => x.bericht),
  ticketWeg: (id: string) => i('ticket-weg', { id }),

  items: (soorten?: string[]) => i<{ items: Item[] }>('items', { soorten }).then((x) => x.items),
  item: (id: string) => i<ItemDetail>('item', { id }),
  itemOpslaan: (item: Partial<Item>) => i<{ item: Item }>('item-opslaan', { item }).then((x) => x.item),
  itemUitgeven: (id: string, houderId: string | null, status?: string) =>
    i<{ item: Item }>('item-uitgeven', { id, houderId, status }).then((x) => x.item),
  itemWeg: (id: string) => i('item-weg', { id }),

  geheimen: () => i<{ geheimen: Geheim[] }>('geheimen').then((x) => x.geheimen),
  geheim: (id: string) => i<GeheimDetail>('geheim', { id }),
  geheimTonen: (id: string, veld: 'waarde' | 'geheim', hoe: 'tonen' | 'kopieren') =>
    i<{ waarde: string }>('geheim-tonen', { id, veld, hoe }).then((x) => x.waarde),
  geheimTotp: (id: string) => i<{ code: string; over: number; periode: number }>('geheim-totp', { id }),
  geheimOpslaan: (geheim: Partial<Geheim>, waarden: Partial<Record<'waarde' | 'totp' | 'geheim', string | null>>) =>
    i<{ geheim: Geheim }>('geheim-opslaan', { geheim, waarden }).then((x) => x.geheim),
  geheimMarkeren: (id: string, reden: string | null) => i('geheim-markeren', { id, reden }),
  geheimWeg: (id: string) => i('geheim-weg', { id }),

  delenZetten: (soort: 'geheim' | 'item' | 'link', id: string, delingen: Deling[]) =>
    i<{ delingen: Deling[] }>('delen-zetten', { soort, id, delingen }).then((x) => x.delingen),

  systemen: () => i<{ systemen: Systeem[] }>('systemen').then((x) => x.systemen),
  systeem: (id: string) => i<SysteemDetail>('systeem', { id }),
  systeemOpslaan: (systeem: Partial<Systeem>) => i<{ systeem: Systeem }>('systeem-opslaan', { systeem }).then((x) => x.systeem),
  systeemWeg: (id: string) => i('systeem-weg', { id }),
  toegangZetten: (lijf: { systeemId: string; persoonId: string; niveau: Niveau; tot?: number | null; notitie?: string | null; ticketId?: string }) =>
    i<{ toegang: Toegang }>('toegang-zetten', lijf).then((x) => x.toegang),
  toegangWeg: (id: string) => i('toegang-weg', { id }),
  toegangAanvragen: (systeemId: string, niveau: Niveau, waarom: string) =>
    i<{ ticket: Ticket }>('toegang-aanvragen', { systeemId, niveau, waarom }).then((x) => x.ticket),

  links: () => i<{ links: Link[] }>('links').then((x) => x.links),
  linkOpslaan: (link: Partial<Link> & { voorIedereen?: boolean }) => i<{ link: Link }>('link-opslaan', { link }).then((x) => x.link),
  linkWeg: (id: string) => i('link-weg', { id }),

  persoon: (id: string) => i<PersoonDetail>('persoon', { id }),
  uitDienst: (id: string, wat: { items: boolean; toegang: boolean; codes: string[] }) =>
    i<{ items: number; toegang: number; codes: number }>('uit-dienst', { id, wat }),
  logboek: (filter: { soort?: string; persoon?: string; q?: string } = {}) =>
    i<{ gebeurtenissen: Gebeurtenis[] }>('logboek', filter).then((x) => x.gebeurtenissen),

  bijlageWeg: (id: string) => i('bijlage-weg', { id }),

  rdw: (kenteken: string) => i<RdwUitkomst>('rdw', { kenteken }),
  qr: (code: string) => i<{ item: { id: string; naam: string; soort: Item['soort']; vestigingId: string | null }; magZien: boolean }>('qr', { code }),
  bewaking: () => i<{ bewaking: Bewaking[] }>('bewaking').then((x) => x.bewaking),

  pakketten: () => i<{ pakketten: Pakket[] }>('pakketten').then((x) => x.pakketten),
  pakketOpslaan: (pakket: Partial<Pakket>) => i<{ pakket: Pakket }>('pakket-opslaan', { pakket }).then((x) => x.pakket),
  pakketWeg: (id: string) => i('pakket-weg', { id }),
  inDienst: (id: string, wat: { systemen: { systeemId: string; niveau: Niveau }[]; geheimen: string[]; items: string[] }) =>
    i<{ toegang: number; codes: number; items: number }>('in-dienst', { id, ...wat }),

  overdracht: () => i<{ punten: OverdrachtPunt[] }>('overdracht').then((x) => x.punten),
  overdrachtOpslaan: (punt: Partial<OverdrachtPunt>) => i<{ punt: OverdrachtPunt }>('overdracht-opslaan', { punt }).then((x) => x.punt),
  overdrachtWeg: (id: string) => i('overdracht-weg', { id }),

  instellingen: () => i<InstellingenStand>('instellingen'),
  /** Eén sleutel tegelijk; het antwoord is de hele stand opnieuw. */
  instellingZetten: (sleutel: string, waarde: string) => i<InstellingenStand>('instelling-zetten', { sleutel, waarde }),

  /* Namens kijken (alleen kijken) en meekijken (met toestemming), 0244.
     Wie wat mag beslist de functie it; de Worker vraagt bij namens-start
     eerst om te bevestigen (428), net als bij een code. */
  namensStart: (profielId: string, reden: string, minuten: NamensDuur) =>
    i<NamensStart>('namens-start', { profielId, app: 'TW10ONLINE', reden, minuten }),
  namensLijst: () => i<NamensLijstAntwoord>('namens-lijst').then((x) => x.sessies),
  namensStop: (id: string) => i('namens-stop', { id }),

  meekijkVraag: (profielId: string, reden: string) => i<MeekijkVraagAntwoord>('meekijk-vraag', { profielId, reden }).then((x) => x.id),
  meekijkVoorMij: () => i<MeekijkVoorMijAntwoord>('meekijk-voor-mij').then((x) => x.verzoeken),
  meekijkStand: (id: string) => i<MeekijkStandAntwoord>('meekijk-stand', { id }).then((x) => x.meekijk),
  meekijkAntwoord: (id: string, akkoord: boolean) => i('meekijk-antwoord', { id, akkoord }),
  meekijkSignaal: (id: string, sdp: string) => i('meekijk-signaal', { id, sdp }),
  meekijkStop: (id: string) => i('meekijk-stop', { id }),
}

interface NamensLijstAntwoord { sessies: NamensSessie[] }
interface MeekijkVraagAntwoord { id: string }
interface MeekijkVoorMijAntwoord { verzoeken: MeekijkVerzoek[] }
interface MeekijkStandAntwoord { meekijk: MeekijkStand }
